import { useForm } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import { FormEvent, useEffect } from 'react'
import { Newspaper } from 'lucide-react'

interface SignupProps {
  errors?: Record<string, string[]>
}

export default function Signup({ errors }: SignupProps) {
  const { data, setData, post, processing } = useForm({
    email_address: '',
    password: '',
    timezone: '',
  })

  // Detect browser timezone on mount
  useEffect(() => {
    try {
      const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
      setData('timezone', browserTimezone)
    } catch (e) {
      // Fallback to default if detection fails
      setData('timezone', 'America/New_York')
    }
  }, [])

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    post('/signup')
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="w-12 h-12 rounded-xl bg-teal-600 flex items-center justify-center">
              <Newspaper className="w-6 h-6 text-white" />
            </div>
          </div>
          <h1 className="text-2xl font-semibold text-foreground">
            Create your account
          </h1>
          <p className="text-muted-foreground mt-2">
            Get started with Inbox Summaries
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-foreground mb-1.5"
            >
              Email
            </label>
            <input
              id="email"
              type="email"
              value={data.email_address}
              onChange={(e) => setData('email_address', e.target.value)}
              className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              required
              autoFocus
            />
            {errors?.email_address && (
              <p className="mt-1 text-sm text-destructive">
                {errors.email_address[0]}
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="password"
              className="block text-sm font-medium text-foreground mb-1.5"
            >
              Password
            </label>
            <input
              id="password"
              type="password"
              value={data.password}
              onChange={(e) => setData('password', e.target.value)}
              className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              required
              minLength={8}
            />
            {errors?.password && (
              <p className="mt-1 text-sm text-destructive">
                {errors.password[0]}
              </p>
            )}
            <p className="mt-1 text-xs text-muted-foreground">
              Must be at least 8 characters
            </p>
          </div>

          <Button type="submit" className="w-full" disabled={processing}>
            {processing ? 'Creating account...' : 'Create account'}
          </Button>
        </form>

        <p className="mt-6 text-center text-sm text-muted-foreground">
          Already have an account?{' '}
          <a href="/login" className="text-primary hover:underline">
            Sign in
          </a>
        </p>
      </div>
    </div>
  )
}
